import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { motion } from "framer-motion";

const Shop = () => {

  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    price: "",
    sort: "newest",
  });

  // 💎 DEMO PRODUCTS (Luxury Collection)
const demoProducts = [
  {
    _id: "1",
    name: "Royal Bridal Lehenga",
    price: 89999,
    category: "bridal-lehenga",
    image:
      "https://images.unsplash.com/photo-1602810319428-019690571b5b?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "2",
    name: "Luxury Wedding Gown",
    price: 129999,
    category: "wedding-gowns",
    image:
      "https://images.unsplash.com/photo-1595777457195-9f8d800c9255?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "3",
    name: "Designer Saree",
    price: 45999,
    category: "designer-sarees",
    image:
      "https://images.unsplash.com/photo-1610030469983-98e550d6193c?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "4",
    name: "Party Wear Lehenga",
    price: 55999,
    category: "party-wear",
    image:
      "https://images.unsplash.com/photo-1610186594416-2a6c2a63f65c?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "5",
    name: "Reception Dress",
    price: 74999,
    category: "reception-dresses",
    image:
      "https://images.unsplash.com/photo-1520975922284-9c3f47f0a6f2?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "6",
    name: "Premium Bridal Set",
    price: 159999,
    category: "bridal-lehenga",
    image:
      "https://images.unsplash.com/photo-1585487000160-6ebcfceb0d03?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "7",
    name: "Luxury Party Gown",
    price: 68999,
    category: "party-wear",
    image:
      "https://images.unsplash.com/photo-1594552072238-b8a33785b261?q=80&w=1200&auto=format&fit=crop",
  },
  {
    _id: "8",
    name: "Heavy Bridal Lehenga",
    price: 179999,
    category: "bridal-lehenga",
    image:
      "https://images.unsplash.com/photo-1610173827046-07f8b7f9c4c3?q=80&w=1200&auto=format&fit=crop",
  },
];

  useEffect(() => {
    fetchProducts();
  }, [filters, location.search]);

  const fetchProducts = () => {
    setLoading(true);

    let filtered = [...demoProducts];

    // CATEGORY FILTER
    if (filters.category) {
      filtered = filtered.filter(
        (p) => p.category === filters.category
      );
    }

    // PRICE FILTER
    if (filters.price === "0-50000") {
      filtered = filtered.filter((p) => p.price <= 50000);
    } else if (filters.price === "50000-100000") {
      filtered = filtered.filter(
        (p) => p.price > 50000 && p.price <= 100000
      );
    } else if (filters.price === "100000+") {
      filtered = filtered.filter((p) => p.price > 100000);
    }

    // SORTING
    if (filters.sort === "price-low") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (filters.sort === "price-high") {
      filtered.sort((a, b) => b.price - a.price);
    }

    setTimeout(() => {
      setProducts(filtered);
      setLoading(false);
    }, 500); // smooth loading feel
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      price: "",
      sort: "newest",
    });
  };

  const categories = [
    "All Collections",
    "Bridal Lehenga",
    "Designer Sarees",
    "Wedding Gowns",
    "Party Wear",
    "Reception Dresses",
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-br from-white to-gray-100 dark:from-black dark:to-gray-900">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-6">
            Luxury Collections
          </h1>

          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Discover premium bridal outfits crafted for unforgettable moments.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-14">

          {/* SIDEBAR */}
          <div className="lg:w-80 bg-white dark:bg-gray-900 p-8 rounded-3xl shadow-2xl">

            <h3 className="text-2xl font-semibold mb-8">Filters</h3>

            <div className="space-y-3">

              {categories.map((cat) => {

                const value =
                  cat === "All Collections"
                    ? ""
                    : cat.toLowerCase().replace(/\s+/g, "-");

                return (
                  <button
                    key={cat}
                    onClick={() =>
                      setFilters({ ...filters, category: value })
                    }
                    className={`w-full text-left px-4 py-3 rounded-xl transition ${
                      filters.category === value
                        ? "bg-yellow-500 text-black"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            <button
              onClick={clearFilters}
              className="mt-10 w-full bg-black text-white py-3 rounded-xl hover:bg-gray-800 transition"
            >
              Clear Filters
            </button>
          </div>

          {/* PRODUCTS */}
          <div className="flex-1">

            <p className="mb-6 text-gray-600">
              {products.length} Products
            </p>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse h-80 bg-gray-200 rounded-2xl"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-10">

                {products.map((product) => (

                  <motion.div
                    key={product._id}
                    whileHover={{ scale: 1.05 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>

                ))}

              </div>
            )}

          </div>

        </div>

      </div>

    </div>
  );
};

export default Shop;